import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  doc,
  onSnapshot,
  Timestamp,
  writeBatch,
  orderBy,
} from 'firebase/firestore';
import { db } from '../../../../utils/init-firebase';
import { useAuth } from '../../../../contexts/AuthContext';
import { Issue, MeetingIssue, COLLECTION_NAMES } from '../../types';
import { getOpenIssuesForRock } from '../../Rocks/rocks.util';

export interface DuplicateIssue {
  id: string;
  name: string;
}

export interface MeetingIssueWithDetails extends MeetingIssue {
  issue?: Issue;
}

export function useMeetingIssues(meetingTimestamp: string | null): {
  meetingIssues: MeetingIssueWithDetails[];
  loading: boolean;
  duplicates: DuplicateIssue[];
  autoAddOpenIssues: () => Promise<DuplicateIssue[]>;
  createAndAddIssue: (issueData: {
    name: string;
    description: string;
    priority: 'low' | 'medium' | 'high';
    relatedRockId?: string | null;
    relatedMetricId?: string | null;
    isRock?: boolean;
    ownerId?: string;
    assignedToUserId?: string;
  }) => Promise<void>;
  resolveIssue: (
    issueId: string,
    resolutionNotes: string
  ) => Promise<{ isLastIssue: boolean; relatedRockId?: string }>;
  actuallyResolveIssue: (issueId: string, resolutionNotes: string) => Promise<void>;
  clearDuplicates: () => void;
  refreshMeetingIssues: () => Promise<void>;
} {
  const { currentUser } = useAuth();
  const [meetingIssues, setMeetingIssues] = useState<MeetingIssueWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [duplicates, setDuplicates] = useState<DuplicateIssue[]>([]);

  const calculateWeeksUnresolved = useCallback((firstOpenedDate: Timestamp | Date | string): number => {
    const opened = firstOpenedDate instanceof Timestamp 
      ? firstOpenedDate.toDate() 
      : new Date(firstOpenedDate);
    const now = new Date();
    const diffMs = now.getTime() - opened.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return Math.floor(diffDays / 7);
  }, []);

  const loadMeetingIssues = useCallback(async () => {
    if (!meetingTimestamp) {
      setMeetingIssues([]);
      setLoading(false);
      return;
    }

    try {
      const timestampObj = Timestamp.fromDate(new Date(meetingTimestamp));

      const q = query(
        collection(db, COLLECTION_NAMES.MEETING_ISSUES),
        where('meetingTimestamp', '==', timestampObj),
        orderBy('createdAt', 'asc')
      );

      const snapshot = await getDocs(q);
      const meetingIssuesData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as MeetingIssue[];

      // Fetch related issue data
      const issuesWithDetails: MeetingIssueWithDetails[] = await Promise.all(
        meetingIssuesData.map(async (meetingIssue) => {
          try {
            const issueRef = doc(db, COLLECTION_NAMES.ISSUES, meetingIssue.issueId);
            const issueDoc = await getDoc(issueRef);
            
            let issue: Issue | undefined;
            if (issueDoc.exists()) {
              issue = {
                id: issueDoc.id,
                ...issueDoc.data(),
              } as Issue;
            }

            return {
              ...meetingIssue,
              issue,
            } as MeetingIssueWithDetails;
          } catch (error) {
            console.error('Error loading issue details:', error);
            return {
              ...meetingIssue,
              issue: undefined,
            } as MeetingIssueWithDetails;
          }
        })
      );

      setMeetingIssues(issuesWithDetails);
    } catch (error) {
      console.error('Error loading meeting issues:', error);
    } finally {
      setLoading(false);
    }
  }, [meetingTimestamp]);

  // Use real-time listener for meeting issues and their related issues
  useEffect(() => {
    if (!meetingTimestamp) {
      setMeetingIssues([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const timestampObj = Timestamp.fromDate(new Date(meetingTimestamp));

    const q = query(
      collection(db, COLLECTION_NAMES.MEETING_ISSUES),
      where('meetingTimestamp', '==', timestampObj),
      orderBy('createdAt', 'asc')
    );

    const loadIssuesWithDetails = async (meetingIssuesData: MeetingIssue[]) => {
      // Fetch fresh issue data for each meeting issue
      const issuesWithDetails: MeetingIssueWithDetails[] = await Promise.all(
        meetingIssuesData.map(async (meetingIssue) => {
          try {
            const issueRef = doc(db, COLLECTION_NAMES.ISSUES, meetingIssue.issueId);
            const issueDoc = await getDoc(issueRef);
            
            let issue: Issue | undefined;
            if (issueDoc.exists()) {
              issue = {
                id: issueDoc.id,
                ...issueDoc.data(),
              } as Issue;
            }

            return {
              ...meetingIssue,
              issue,
            } as MeetingIssueWithDetails;
          } catch (error) {
            console.error('Error loading issue details:', error);
            return {
              ...meetingIssue,
              issue: undefined,
            } as MeetingIssueWithDetails;
          }
        })
      );

      setMeetingIssues(issuesWithDetails);
      setLoading(false);
    };

    // Listen to meeting issues changes
    const unsubscribeMeetingIssues = onSnapshot(
      q,
      async (snapshot) => {
        const meetingIssuesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as MeetingIssue[];

        await loadIssuesWithDetails(meetingIssuesData);
      },
      (error) => {
        console.error('Error in meeting issues listener:', error);
        setLoading(false);
      }
    );

    // Also set up listeners for individual issues to catch status changes
    // We'll set these up after the initial load
    let issueUnsubscribes: (() => void)[] = [];
    
    const setupIssueListeners = async () => {
      // First, get the current meeting issues
      const snapshot = await getDocs(q);
      const meetingIssuesData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as MeetingIssue[];

      // Clean up old listeners
      issueUnsubscribes.forEach((unsub) => unsub());
      issueUnsubscribes = [];

      // Set up listeners for each issue
      meetingIssuesData.forEach((meetingIssue) => {
        const issueRef = doc(db, COLLECTION_NAMES.ISSUES, meetingIssue.issueId);
        const unsubscribe = onSnapshot(
          issueRef,
          () => {
            // When an issue changes, reload all meeting issues
            getDocs(q).then(async (snapshot) => {
              const updatedMeetingIssues = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              })) as MeetingIssue[];
              await loadIssuesWithDetails(updatedMeetingIssues);
            });
          },
          (error) => {
            console.error('Error in issue listener:', error);
          }
        );
        issueUnsubscribes.push(unsubscribe);
      });

      await loadIssuesWithDetails(meetingIssuesData);
    };

    setupIssueListeners();

    return () => {
      unsubscribeMeetingIssues();
      issueUnsubscribes.forEach((unsub) => unsub());
    };
  }, [meetingTimestamp]);

  const autoAddOpenIssues = useCallback(async (): Promise<DuplicateIssue[]> => {
    console.log('autoAddOpenIssues', meetingTimestamp, currentUser);
    if (!meetingTimestamp || !currentUser || !currentUser.uid) return [];

    try {
      // Get all open issues
      const openIssuesQuery = query(
        collection(db, COLLECTION_NAMES.ISSUES),
        where('status', '!=', 'solved')
      );
      const openIssuesSnapshot = await getDocs(openIssuesQuery);
      const openIssues = openIssuesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Issue[];

      if (openIssues.length === 0) return [];

      // Get existing meeting issues
      const timestampObj = Timestamp.fromDate(new Date(meetingTimestamp));
      const existingQuery = query(
        collection(db, COLLECTION_NAMES.MEETING_ISSUES),
        where('meetingTimestamp', '==', timestampObj)
      );
      const existingSnapshot = await getDocs(existingQuery);
      const existingIds = new Set(
        existingSnapshot.docs.map((doc) => doc.data().issueId)
      );

      const duplicateIssues: DuplicateIssue[] = openIssues
        .filter((issue) => existingIds.has(issue.id))
        .map((issue) => ({ id: issue.id, name: issue.name }));

      const newIssues = openIssues.filter((issue) => !existingIds.has(issue.id));

      // Add new issues to meeting
      if (newIssues.length > 0) {
        const batch = writeBatch(db);
        newIssues.forEach((issue) => {
          const newMeetingIssueRef = doc(collection(db, COLLECTION_NAMES.MEETING_ISSUES));
          batch.set(newMeetingIssueRef, {
            meetingTimestamp: timestampObj,
            issueId: issue.id,
            addedAutomatically: true,
            addedByUserId: currentUser.uid,
            weeksUnresolvedAtAdd: calculateWeeksUnresolved(issue.firstOpenedDate),
            resolvedInThisMeeting: false,
            createdAt: Timestamp.now(),
          });
        });
        await batch.commit();
      }

      await loadMeetingIssues();
      setDuplicates(duplicateIssues);
      return duplicateIssues;
    } catch (error) {
      console.error('Error auto-adding issues:', error);
      return [];
    }
  }, [meetingTimestamp, currentUser, calculateWeeksUnresolved, loadMeetingIssues]);

  const createAndAddIssue = useCallback(
    async (issueData: {
      name: string;
      description: string;
      priority: 'low' | 'medium' | 'high';
      relatedRockId?: string | null;
      relatedMetricId?: string | null;
      isRock?: boolean;
      ownerId?: string;
      assignedToUserId?: string;
    }) => {
      if (!meetingTimestamp || !currentUser || !currentUser.uid) {
        throw new Error('Meeting must be started and user must be logged in to add issues');
      }

      try {
        const now = Timestamp.now();
        const timestampObj = Timestamp.fromDate(new Date(meetingTimestamp));

        // Create new issue
        const newIssueRef = doc(collection(db, COLLECTION_NAMES.ISSUES));
        const newIssue = {
          ...issueData,
          ownerId: issueData.ownerId || currentUser.uid,
          assignedToUserId: issueData.assignedToUserId || issueData.ownerId || currentUser.uid,
          status: 'open' as const,
          firstOpenedDate: now,
          createdInMeetingId: timestampObj,
          isRock: !!issueData.relatedRockId || !!issueData.isRock,
          isMetric: !!issueData.relatedMetricId,
          createdAt: now,
          updatedAt: now,
        };
        const issueDocRef = await addDoc(collection(db, COLLECTION_NAMES.ISSUES), newIssue);

        // Add to meeting issues
        await addDoc(collection(db, COLLECTION_NAMES.MEETING_ISSUES), {
          meetingTimestamp: timestampObj,
          issueId: issueDocRef.id,
          addedAutomatically: false,
          addedByUserId: currentUser.uid,
          weeksUnresolvedAtAdd: 0,
          resolvedInThisMeeting: false,
          createdAt: now,
        });

        await loadMeetingIssues();
      } catch (error) {
        console.error('Error creating and adding issue:', error);
        throw error;
      }
    },
    [meetingTimestamp, currentUser, loadMeetingIssues]
  );

  const resolveIssue = useCallback(
    async (
      issueId: string,
      resolutionNotes: string
    ): Promise<{ isLastIssue: boolean; relatedRockId?: string }> => {
      if (!meetingTimestamp) {
        return { isLastIssue: false };
      }

      try {
        // Get the issue first to check its relatedRockId
        const issueRef = doc(db, COLLECTION_NAMES.ISSUES, issueId);
        const issueDoc = await getDoc(issueRef);
        
        if (!issueDoc.exists()) {
          throw new Error('Issue not found');
        }

        const issueData = issueDoc.data() as Issue;
        const relatedRockId = issueData.relatedRockId;

        // Check for incomplete action items
        const actionItemsQuery = query(
          collection(db, COLLECTION_NAMES.ACTION_ITEMS),
          where('linkedIssueId', '==', issueId),
          where('complete', '==', false),
          where('dropped', '==', false)
        );
        const actionItemsSnapshot = await getDocs(actionItemsQuery);

        if (!actionItemsSnapshot.empty) {
          const actionItemList = actionItemsSnapshot.docs
            .map((doc) => {
              const data = doc.data();
              return `- ${data.description || data.task || 'Action Item'}`;
            })
            .join('\n');
          alert(`Cannot resolve this issue. The following action items are still incomplete:\n\n${actionItemList}\n\nPlease complete or drop these action items before resolving the issue.`);
          return { isLastIssue: false };
        }

        // Check if this is the last open issue for the related rock BEFORE resolving
        let isLastIssue = false;
        if (relatedRockId) {
          const openIssues = await getOpenIssuesForRock(relatedRockId);
          // Filter out the current issue we're about to resolve
          const otherOpenIssues = openIssues.filter((issue) => issue.id !== issueId);
          isLastIssue = otherOpenIssues.length === 0;
        }

        // If it's the last issue, return metadata without resolving
        // The parent component will handle showing the ResolveIssueModal with isLastIssue prop
        if (isLastIssue) {
          return {
            isLastIssue: true,
            relatedRockId: relatedRockId || undefined,
          };
        }

        // Not the last issue - proceed with resolution
        const now = Timestamp.now();
        const timestampObj = Timestamp.fromDate(new Date(meetingTimestamp));

        // Use batch to update both collections
        const batch = writeBatch(db);

        // Update meeting issue
        const meetingIssueQuery = query(
          collection(db, COLLECTION_NAMES.MEETING_ISSUES),
          where('meetingTimestamp', '==', timestampObj),
          where('issueId', '==', issueId)
        );
        const meetingIssueSnapshot = await getDocs(meetingIssueQuery);
        if (!meetingIssueSnapshot.empty) {
          const meetingIssueRef = meetingIssueSnapshot.docs[0].ref;
          batch.update(meetingIssueRef, {
            resolvedInThisMeeting: true,
            resolvedAt: now,
            resolutionNotes,
          });
        }

        // Update issue status
        batch.update(issueRef, {
          status: 'solved',
          solvedAt: now,
          updatedAt: now,
          resolutionNotes,
        });

        await batch.commit();
        await loadMeetingIssues();

        return {
          isLastIssue: false,
          relatedRockId: relatedRockId || undefined,
        };
      } catch (error) {
        console.error('Error resolving issue:', error);
        throw error;
      }
    },
    [meetingTimestamp, loadMeetingIssues]
  );

  /**
   * Actually resolve an issue (without checking for last issue)
   * Used by ResolveIssueModal after user confirms action for the last issue
   */
  const actuallyResolveIssue = useCallback(
    async (issueId: string, resolutionNotes: string): Promise<void> => {
      if (!meetingTimestamp) {
        throw new Error('No meeting timestamp');
      }

      try {
        const issueRef = doc(db, COLLECTION_NAMES.ISSUES, issueId);
        const issueDoc = await getDoc(issueRef);
        
        if (!issueDoc.exists()) {
          throw new Error('Issue not found');
        }

        // Check for incomplete action items
        const actionItemsQuery = query(
          collection(db, COLLECTION_NAMES.ACTION_ITEMS),
          where('linkedIssueId', '==', issueId),
          where('complete', '==', false),
          where('dropped', '==', false)
        );
        const actionItemsSnapshot = await getDocs(actionItemsQuery);

        if (!actionItemsSnapshot.empty) {
          const actionItemList = actionItemsSnapshot.docs
            .map((doc) => {
              const data = doc.data();
              return `- ${data.description || data.task || 'Action Item'}`;
            })
            .join('\n');
          throw new Error(`Cannot resolve this issue. The following action items are still incomplete:\n\n${actionItemList}\n\nPlease complete or drop these action items before resolving the issue.`);
        }

        const now = Timestamp.now();
        const timestampObj = Timestamp.fromDate(new Date(meetingTimestamp));

        // Use batch to update both collections
        const batch = writeBatch(db);

        // Update meeting issue
        const meetingIssueQuery = query(
          collection(db, COLLECTION_NAMES.MEETING_ISSUES),
          where('meetingTimestamp', '==', timestampObj),
          where('issueId', '==', issueId)
        );
        const meetingIssueSnapshot = await getDocs(meetingIssueQuery);
        if (!meetingIssueSnapshot.empty) {
          const meetingIssueRef = meetingIssueSnapshot.docs[0].ref;
          batch.update(meetingIssueRef, {
            resolvedInThisMeeting: true,
            resolvedAt: now,
            resolutionNotes,
          });
        }

        // Update issue status
        batch.update(issueRef, {
          status: 'solved',
          solvedAt: now,
          updatedAt: now,
          resolutionNotes,
        });

        await batch.commit();
        await loadMeetingIssues();
      } catch (error) {
        console.error('Error resolving issue:', error);
        throw error;
      }
    },
    [meetingTimestamp, loadMeetingIssues]
  );

  const clearDuplicates = useCallback(() => {
    setDuplicates([]);
  }, []);

  return {
    meetingIssues,
    loading,
    duplicates,
    autoAddOpenIssues,
    createAndAddIssue,
    resolveIssue: resolveIssue as (
      issueId: string,
      resolutionNotes: string
    ) => Promise<{ isLastIssue: boolean; relatedRockId?: string }>,
    actuallyResolveIssue,
    clearDuplicates,
    refreshMeetingIssues: loadMeetingIssues,
  };
}

